// Única fuente de verdad para el precio y los badges de descuento que
// muestran las cards de producto (StorePage, LoMasComprado, ModalProductDetail,
// ProductsComponent, ModalStoreCart, OrderDetailModal). Antes de este módulo
// (Sprint 0 del rediseño, OPEN_MATIC_REDESIGN_PLAN_2.md §4.2) esta lógica
// estaba copiada 6 veces con al menos 3 variantes de comportamiento distintas
// entre sí — ver el historial de commits de este archivo para el detalle de
// qué se unificó y por qué.
//
// resolverPrecioProducto() replica DELIBERADAMENTE la precedencia de
// calculateTotals en src/_zustand/store.js (porcentaje > monto fijo >
// descuento de máquina/tipo 3 > oferta directa > precio base), no la que
// tenían las cards antes de este cambio — para que el precio que se muestra
// coincida con lo que se cobra al pagar. Se validó además contra
// getUnitPrice en Sima-appmovil/src/utils/pricing.js (la referencia
// canónica cross-plataforma): coincide en los dos primeros pasos
// (porcentaje, monto fijo); el paso de "descuento de máquina" es exclusivo
// de este backend/web y SÍ existe en calculateTotals, así que se conserva.
//
// El 2x1 (descuento_dosxuno) NO participa acá a propósito: ni calculateTotals
// ni getUnitPrice tocan el precio unitario por 2x1 — ambos cobran menos
// UNIDADES (cantidad efectiva = ceil(cant/2)), no un precio unitario menor.
// Las 4 cards que antes tenían una rama explícita "dosxuno>0 && cant==2 ->
// mostrar producto_precio" la tenían de adorno: sin esa rama, el fallback
// final del ladder ya resuelve al mismo producto_precio. De ahí que esas
// mismas cards tuvieran 3 variantes distintas de esa condición (==2,
// %2===0, >=2) sin que ninguna cambiara jamás el precio mostrado.
export const resolverPrecioProducto = (producto) => {
  const descuentoPorcentaje = Number(producto?.descuento_porcentaje) || 0;
  const descuentoMontoFijo = Number(producto?.descuento_monto_fijo) || 0;
  const descuentoPrecioFinal = Number(producto?.descuento_precio_final) || 0;
  const productoOferta = Number(producto?.producto_oferta) || 0;
  const productoPrecio = Number(producto?.producto_precio) || 0;

  let precioFinal;
  if (descuentoPorcentaje > 0) {
    // Igual que getUnitPrice en Sima-appmovil: si el backend marca
    // descuento_porcentaje>0 pero no entrega un descuento_precio_final
    // válido (falta, 0 o negativo), NO se interpreta como gratis — se cae
    // al precio base.
    precioFinal = descuentoPrecioFinal > 0 ? descuentoPrecioFinal : productoPrecio;
  } else if (descuentoMontoFijo > 0) {
    precioFinal = Math.max(0, productoPrecio - descuentoMontoFijo);
  } else if (descuentoPrecioFinal > 0 && descuentoPrecioFinal < productoPrecio) {
    // Descuento de máquina / tipo 3 — igual que calculateTotals.
    precioFinal = descuentoPrecioFinal;
  } else if (productoOferta > 0 && productoOferta < productoPrecio) {
    precioFinal = productoOferta;
  } else {
    precioFinal = productoPrecio;
  }

  return {
    precioFinal,
    precioOriginal: productoPrecio,
    tieneDescuento: precioFinal !== productoPrecio,
  };
};

// Unidades que realmente se cobran. El 2x1 no baja el precio unitario: cobra
// ceil(cant/2) unidades (ver el comentario de cabecera). Única fuente de
// verdad de esa regla — calculateTotals en src/_zustand/store.js y los
// subtotales por línea del carrito la consumen desde acá en vez de repetir
// el `descuento_dosxuno > 0 && cant >= 2 ? ceil : cant` (que ya había
// divergido en 3 variantes distintas entre pantallas).
export const cantidadFacturable = (producto, cantidad) => {
  const cant = Number(cantidad) || 0;
  const descuentoDosxuno = Number(producto?.descuento_dosxuno) || 0;
  return descuentoDosxuno > 0 && cant >= 2 ? Math.ceil(cant / 2) : cant;
};

// Subtotal de una línea del carrito: lo que esa línea aporta al total que se
// COBRA. Existe porque las cards del carrito mostraban el subtotal con un
// ladder propio y divergente del de calculateTotals (bug real: con
// monto_fijo=200 sobre precio 1000 y cant=2 la card mostraba $2.000 mientras
// el carrito cobraba $1.600).
//
// `subtotalOriginal` usa la cantidad COMPLETA a propósito: es el precio
// tachado ("antes"), que debe reflejar lo que costarían todas las unidades
// sin ningún descuento — incluidas las que el 2x1 regala.
export const resolverSubtotalProducto = (producto, cantidad) => {
  const { precioFinal, precioOriginal } = resolverPrecioProducto(producto);
  const cant = Number(cantidad) || 0;
  const subtotal = precioFinal * cantidadFacturable(producto, cant);
  const subtotalOriginal = precioOriginal * cant;

  return {
    subtotal,
    subtotalOriginal,
    tieneDescuento: subtotal !== subtotalOriginal,
  };
};

// Badges de descuento — este ladder SÍ era idéntico en los 6 archivos
// originales, sin divergencias de comportamiento (a diferencia del precio).
// Devuelve una lista porque varios badges pueden coexistir: el "% off"
// calculado desde producto_oferta (solo cuando la oferta es el precio que
// realmente se aplica), el de 2x1 (ortogonal al precio unitario) y el badge
// "principal" por prioridad del ladder de descuentos. La mayoría de los
// estados produce uno solo.
export const resolverBadgesDescuento = (producto) => {
  const descuentoDosxuno = Number(producto?.descuento_dosxuno) || 0;
  const descuentoPorcentaje = Number(producto?.descuento_porcentaje) || 0;
  const descuentoMontoFijo = Number(producto?.descuento_monto_fijo) || 0;
  const descuentoPrecioFinal = Number(producto?.descuento_precio_final) || 0;
  const productoOferta = Number(producto?.producto_oferta) || 0;
  const productoPrecio = Number(producto?.producto_precio) || 0;

  const badges = [];

  // El badge de "% off" por oferta directa se emite sólo cuando la oferta es
  // efectivamente el precio que se está aplicando — se pregunta al propio
  // resolverPrecioProducto en vez de re-derivar la precedencia acá. Así el
  // porcentaje mostrado nunca puede contradecir el precio mostrado/cobrado:
  //  - Con un descuento de mayor precedencia (porcentaje, monto fijo,
  //    precio_final de máquina) el precio NO sale de la oferta, así que este
  //    badge no aparece y el porcentaje engañoso desaparece con él.
  //  - Con 2x1 + oferta directa el precio unitario SÍ baja por la oferta, y
  //    antes este badge se ocultaba sólo por la presencia del 2x1, dejando
  //    ese ahorro sin ninguna señal visual aunque el subtotal ya lo cobraba
  //    más barato (el 2x1 es ortogonal, ver más abajo).
  // El tope `productoOferta < productoPrecio` sigue siendo necesario aparte
  // de la comparación con el precio aplicado: con una oferta corrupta IGUAL
  // al precio base el ladder cae al precio base, que coincide con la oferta,
  // y sin este tope se emitiría un badge de 0% en un producto sin descuento.
  const { precioFinal } = resolverPrecioProducto(producto);
  if (
    productoOferta > 0 &&
    productoOferta < productoPrecio &&
    precioFinal === productoOferta
  ) {
    badges.push({
      tipo: "porcentaje-oferta",
      percentage: (1 - productoOferta / productoPrecio) * 100,
    });
  }

  // El 2x1 es ORTOGONAL al ladder de descuentos de precio unitario: no baja
  // el precio por unidad, cobra menos unidades (ver cantidadFacturable), y
  // calculateTotals lo aplica SIEMPRE, incluso combinado con un porcentaje o
  // un monto fijo. Por eso su badge se emite por separado y no como una rama
  // más del else-if de abajo: cuando estaba dentro de la cadena, un producto
  // con 2x1 + porcentaje perdía el badge de 2x1 en pantalla mientras el
  // carrito seguía cobrándole la mitad de las unidades (bug encontrado en
  // code review tras unificar los badges de ModalProductDetail, que lo tenía
  // como condición independiente).
  if (descuentoDosxuno > 0) {
    badges.push({ tipo: "2x1" });
  }

  if (descuentoPorcentaje > 0) {
    badges.push({ tipo: "porcentaje", percentage: descuentoPorcentaje });
  } else if (descuentoMontoFijo > 0) {
    badges.push({ tipo: "oferta" });
  } else if (descuentoPrecioFinal > 0 && descuentoPrecioFinal < productoPrecio) {
    badges.push({ tipo: "oferta" });
  }

  return badges;
};

// ⚠️ SIN CONSUMIDORES TODAVÍA (verificado por grep: ningún import fuera de
// su propio test). Es andamiaje del Sprint 0 del rediseño para los badges de
// stock ("agotado" / "últimas unidades") que la UI todavía no muestra en
// ninguna card. Se conserva a propósito —está documentado y testeado— pero
// NO es código vivo: cablearlo implica decidir dónde y cómo se ven esos
// badges, que es una decisión de diseño pendiente, no parte de este fix.
// Si el rediseño cambia de rumbo y estos estados no se van a usar, esta
// función y su bloque de tests deberían borrarse en vez de quedar de adorno.
//
// Estados de disponibilidad — presentación nueva sobre un dato que YA llega
// del backend (`stock` en stockProductosAPP) y que hoy solo se usa para
// bloquear agregar de más al carrito (ver store.js), nunca se muestra.
// Sin `stock` en el producto (undefined/null) se asume disponible: hay
// pantallas que todavía no traen ese campo y no deben mostrar "agotado" por
// default.
export const resolverDisponibilidad = (producto, umbralBajo = 3) => {
  const stock = producto?.stock;
  if (stock === undefined || stock === null || stock === "") {
    return { estado: "disponible", stock: null };
  }

  const stockNum = Number(stock);
  if (!Number.isFinite(stockNum)) {
    return { estado: "disponible", stock: null };
  }
  if (stockNum <= 0) {
    return { estado: "agotado", stock: stockNum };
  }
  if (stockNum <= umbralBajo) {
    return { estado: "ultimas", stock: stockNum };
  }
  return { estado: "disponible", stock: stockNum };
};
